import React from 'react';

import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  ImageList,
  ImageListItem,
  ImageListItemBar,
  Tooltip,
  Typography,
} from '@mui/material';

import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import { ChangeEvent, useRef, useState } from 'react';

import type { ChecklistImage } from '../../types/checklistImages';

interface ChecklistImageUploaderProps {
  images: ChecklistImage[];
  onChange: (images: ChecklistImage[]) => void;

  maxImages?: number;
  maxFileSizeMb?: number;
  disabled?: boolean;

  emptyMessage?: string;

  onValidationError?: (message: string) => void;
}

export function ChecklistImageUploader({
  images,
  onChange,
  maxImages = 10,
  maxFileSizeMb = 10,
  disabled = false,
  emptyMessage = 'Nenhuma imagem adicionada.',
  onValidationError,
}: ChecklistImageUploaderProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);

  const [selectedImage, setSelectedImage] = useState<ChecklistImage | null>(
    null,
  );

  const notifyValidationError = (message: string): void => {
    if (onValidationError) {
      onValidationError(message);
      return;
    }

    console.error(message);
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const files = Array.from(event.target.files ?? []);

    // Allows the same file to be selected again.
    event.target.value = '';

    if (files.length === 0) {
      return;
    }

    const availableSlots = maxImages - images.length;

    if (availableSlots <= 0) {
      notifyValidationError(
        `É permitido adicionar no máximo ${maxImages} imagens.`,
      );
      return;
    }

    const maxFileSizeBytes = maxFileSizeMb * 1024 * 1024;

    const acceptedFiles: File[] = [];

    for (const file of files) {
      if (!file.type.startsWith('image/')) {
        notifyValidationError(
          `${file.name} não é um arquivo de imagem válido.`,
        );
        continue;
      }

      if (file.size > maxFileSizeBytes) {
        notifyValidationError(
          `${file.name} ultrapassa o limite de ${maxFileSizeMb} MB.`,
        );
        continue;
      }

      acceptedFiles.push(file);
    }

    const newImages: ChecklistImage[] = acceptedFiles
      .slice(0, availableSlots)
      .map(file => ({
        id: crypto.randomUUID(),
        file,
        name: file.name,
        size: file.size,
        previewUrl: URL.createObjectURL(file),
        persisted: false,
      }));

    onChange([...images, ...newImages]);
  };

  const handleRemoveImage = (imageId: string): void => {
    const imageToRemove = images.find(image => image.id === imageId);

    if (
      imageToRemove &&
      imageToRemove.file &&
      imageToRemove.previewUrl.startsWith('blob:')
    ) {
      URL.revokeObjectURL(imageToRemove.previewUrl);
    }

    if (selectedImage?.id === imageId) {
      setSelectedImage(null);
    }

    onChange(images.filter(image => image.id !== imageId));
  };

  const formatFileSize = (size?: number): string | undefined => {
    if (size === undefined) {
      return undefined;
    }

    return `${(size / 1024 / 1024).toFixed(2)} MB`;
  };

  return (
    <>
      <Box
        sx={{
          width: '100%',
          maxWidth: '1236px',
          border: '1px solid',
          borderColor: 'grey',
          borderRadius: 2,
          p: 2,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            mb: images.length > 0 ? 2 : 0,
          }}
        >
          <Box>
            <Typography variant="body2" color="text.secondary">
              {images.length}/{maxImages} imagens
            </Typography>
          </Box>

          <Button
            type="button"
            variant="contained"
            startIcon={<AddPhotoAlternateIcon />}
            disabled={disabled || images.length >= maxImages}
            onClick={() => inputRef.current?.click()}
          >
            Adicionar imagens
          </Button>
        </Box>

        {images.length === 0 ? (
          <Box
            sx={{
              py: 5,
              textAlign: 'center',
              color: 'text.secondary',
            }}
          >
            {emptyMessage}
          </Box>
        ) : (
          <ImageList
            cols={4}
            gap={12}
            sx={{
              width: '100%',
              m: 0,
            }}
          >
            {images.map(image => (
              <ImageListItem
                key={image.id}
                sx={{
                  borderRadius: 2,
                  overflow: 'hidden',
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Box
                  component="button"
                  type="button"
                  onClick={() => setSelectedImage(image)}
                  sx={{
                    display: 'block',
                    width: '100%',
                    height: 180,
                    p: 0,
                    border: 0,
                    backgroundColor: 'transparent',
                    cursor: 'pointer',
                  }}
                >
                  <Box
                    component="img"
                    src={image.previewUrl}
                    alt={image.name}
                    loading="lazy"
                    sx={{
                      display: 'block',
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                  />
                </Box>

                <ImageListItemBar
                  title={image.name}
                  subtitle={formatFileSize(image.size)}
                  actionIcon={
                    <Tooltip title="Remover imagem">
                      <IconButton
                        type="button"
                        aria-label={`Remover ${image.name}`}
                        disabled={disabled}
                        onClick={() => handleRemoveImage(image.id)}
                        sx={{ color: 'common.white' }}
                      >
                        <DeleteOutlineIcon />
                      </IconButton>
                    </Tooltip>
                  }
                />
              </ImageListItem>
            ))}
          </ImageList>
        )}
      </Box>

      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        multiple
        hidden
        disabled={disabled}
        onChange={handleFileChange}
      />

      <Dialog
        open={selectedImage !== null}
        onClose={() => setSelectedImage(null)}
        maxWidth="xl"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            width: '100%',
            maxWidth: '1200px',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          <Typography
            component="span"
            variant="h6"
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {selectedImage?.name}
          </Typography>

          <IconButton
            type="button"
            aria-label="Fechar visualização"
            onClick={() => setSelectedImage(null)}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent
          dividers
          sx={{
            display: 'flex',
            justifyContent: 'center',
            backgroundColor: 'grey.100',
          }}
        >
          {selectedImage && (
            <Box
              component="img"
              src={selectedImage.previewUrl}
              alt={selectedImage.name}
              sx={{
                display: 'block',
                maxWidth: '100%',
                maxHeight: '75vh',
                objectFit: 'contain',
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
